import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Download, Search, RefreshCw, Calendar, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { PageHeader } from '../components/layout/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState, EmptyState } from '../components/common/ErrorState';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getFavoritos, saveFavoritos } from '../services/storage';
import { fetchWithCache } from '../services/cacheService';
import { toast } from 'sonner';
import { API_BASE_URL } from '../config/api';

// IDs de los datasets de precios por año
const DATASET_IDS = {
  2026: '125d34ce-bd0f-423a-9242-b15f826a6750',
  2025: '6ef31279-7dd5-4c19-ad77-d5defe92b46e',
  2024: '0723d38b-dbea-4d58-b864-6943c972b2e0',
};

// Años disponibles
const YEARS_AVAILABLE = Object.keys(DATASET_IDS).map(Number).sort((a, b) => b - a);

// Meses en español (para mostrar en el selector)
const MESES_NOMBRES = {
  1: 'Enero', 2: 'Febrero', 3: 'Marzo', 4: 'Abril',
  5: 'Mayo', 6: 'Junio', 7: 'Julio', 8: 'Agosto',
  9: 'Septiembre', 10: 'Octubre', 11: 'Noviembre', 12: 'Diciembre'
};

// Meses en español (para parsear nombres de recursos)
const MESES_PARSE = {
  'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4,
  'mayo': 5, 'junio': 6, 'julio': 7, 'agosto': 8,
  'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12
};

/**
 * Parsea el nombre del recurso para extraer el mes
 * Ejemplo: "Precios diarios de febrero de 2026" -> { mes: 2, nombre: 'febrero' }
 */
function parseMesFromResourceName(name) {
  if (!name) return null;
  const nameLower = name.toLowerCase();
  
  for (const [mesNombre, mesNum] of Object.entries(MESES_PARSE)) {
    if (nameLower.includes(mesNombre)) {
      return { mes: mesNum, nombre: mesNombre };
    }
  }
  return null;
}

export function PreciosPage() {
  const [productos, setProductos] = useState([]);
  const [selectedProducto, setSelectedProducto] = useState('');
  const [preciosData, setPreciosData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMeses, setLoadingMeses] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [favoritos, setFavoritos] = useState(() => getFavoritos());
  
  // Recursos disponibles por año (detectados de la API)
  const [recursosDisponibles, setRecursosDisponibles] = useState({});
  
  // Selector de año y mes
  const [selectedYear, setSelectedYear] = useState(YEARS_AVAILABLE[0]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Recargar favoritos al montar
  useEffect(() => {
    setFavoritos(getFavoritos());
  }, []);

  // Cargar recursos disponibles del año seleccionado desde CKAN
  async function loadRecursosDisponibles(year) {
    const datasetId = DATASET_IDS[year];
    if (!datasetId) return {};
    
    try {
      // Consultar la API de CKAN para obtener los recursos del dataset
      const apiUrl = `https://datos.tenerife.es/ckan/api/action/package_show?id=${datasetId}`;
      const proxyUrl = `${API_BASE_URL}/api/ckan-proxy?url=${encodeURIComponent(apiUrl)}`;
      
      const result = await fetchWithCache(proxyUrl);
      const resources = result.data.result?.resources || [];
      
      // Filtrar solo recursos JSON de precios y extraer información del mes
      const mesesRecursos = {};
      
      for (const resource of resources) {
        // Solo procesar archivos JSON que contengan "precios diarios"
        if (resource.format?.toUpperCase() !== 'JSON') continue;
        if (!resource.name?.toLowerCase().includes('precios diarios')) continue;
        
        const mesInfo = parseMesFromResourceName(resource.name);
        if (mesInfo) {
          // Si hay múltiples recursos para el mismo mes, usar el más reciente
          if (!mesesRecursos[mesInfo.mes] || 
              new Date(resource.created) > new Date(mesesRecursos[mesInfo.mes].created)) {
            mesesRecursos[mesInfo.mes] = {
              resourceId: resource.id,
              nombre: mesInfo.nombre,
              created: resource.created,
              url: resource.url
            };
          }
        }
      }
      
      return mesesRecursos;
    } catch (err) {
      console.error('Error cargando recursos de CKAN:', err);
      return {};
    }
  }

  // Detectar meses disponibles cuando cambia el año
  useEffect(() => {
    async function detectarMeses() {
      setLoadingMeses(true);
      
      // Si ya tenemos los recursos cacheados para este año, usarlos
      if (recursosDisponibles[selectedYear]) {
        const meses = Object.keys(recursosDisponibles[selectedYear]).map(Number).sort((a, b) => b - a);
        setAvailableMonths(meses);
        if (meses.length > 0 && (!selectedMonth || !meses.includes(selectedMonth))) {
          setSelectedMonth(meses[0]); // Seleccionar el mes más reciente
        }
        setLoadingMeses(false);
        return;
      }
      
      // Cargar recursos desde CKAN
      const recursos = await loadRecursosDisponibles(selectedYear);
      
      // Cachear los recursos
      setRecursosDisponibles(prev => ({
        ...prev,
        [selectedYear]: recursos
      }));
      
      // Actualizar meses disponibles
      const meses = Object.keys(recursos).map(Number).sort((a, b) => b - a);
      setAvailableMonths(meses);
      
      // Seleccionar el mes más reciente si no hay selección válida
      if (meses.length > 0 && (!selectedMonth || !meses.includes(selectedMonth))) {
        setSelectedMonth(meses[0]);
      }
      
      setLoadingMeses(false);
    }
    
    detectarMeses();
  }, [selectedYear]);

  // Cargar datos cuando cambia año o mes
  useEffect(() => {
    if (selectedMonth && availableMonths.includes(selectedMonth) && !loadingMeses) {
      loadProductos();
    }
  }, [selectedYear, selectedMonth, loadingMeses]);

  async function loadProductos() {
    setLoading(true);
    setError(null);
    setProductos([]);
    setPreciosData(null);
    setSelectedProducto('');
    
    try {
      // Obtener el recurso del mes seleccionado (detectado dinámicamente)
      const recursoMes = recursosDisponibles[selectedYear]?.[selectedMonth];
      if (!recursoMes?.url) {
        throw new Error(`No hay datos disponibles para ${MESES_NOMBRES[selectedMonth]} ${selectedYear}`);
      }

      // Usar proxy del backend con caché
      const API_PROXY = `${API_BASE_URL}/api/ckan-proxy`;
      const result = await fetchWithCache(`${API_PROXY}?url=${encodeURIComponent(recursoMes.url)}`);
      
      const data = result.data;
      
      // El formato es: { producto: [ { producto_nombre, origen: [ { historico_precios: [...] } ] } ] }
      const productosRaw = data.producto || [];
      
      // Procesar productos
      const productosList = productosRaw.map((prod, index) => {
        const nombre = prod.producto_nombre;
        
        // Obtener todos los precios de todos los orígenes
        const todosPrecios = [];
        (prod.origen || []).forEach(origen => {
          (origen.historico_precios || []).forEach(hp => {
            todosPrecios.push({
              fecha: hp.precio_fecha,
              precio: hp.precio_moda || hp.precio_maximo || hp.precio_minimo,
              min: hp.precio_minimo,
              max: hp.precio_maximo,
              origen: origen.producto_origen
            });
          });
        });
        
        // Ordenar por fecha (más reciente primero)
        todosPrecios.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        
        return {
          id: index + 1,
          nombre,
          codigo: prod.producto_codigo,
          precios: todosPrecios,
          ultimoPrecio: todosPrecios[0]?.precio || 0,
          ultimaFecha: todosPrecios[0]?.fecha || null
        };
      });
      
      // Ordenar por nombre
      productosList.sort((a, b) => a.nombre.localeCompare(b.nombre));
      
      setProductos(productosList);
      
      // Auto-seleccionar favorito si existe
      if (favoritos.productoFavorito) {
        const fav = productosList.find(p => p.nombre === favoritos.productoFavorito);
        if (fav) {
          setSelectedProducto(fav.id.toString());
        }
      }
    } catch (err) {
      console.error('Error cargando precios:', err);
      setError(err.message || 'Error al cargar los datos de precios');
    } finally {
      setLoading(false);
    }
  }

  // Filtrar productos por búsqueda
  const filteredProductos = useMemo(() => {
    if (!searchTerm) return productos;
    return productos.filter(p => 
      p.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [productos, searchTerm]);

  // Cargar datos de un producto específico para análisis
  async function loadPreciosProducto(productoId) {
    const producto = productos.find(p => p.id === parseInt(productoId));
    if (!producto) return;
    
    // Ya tenemos los precios del producto
    const chartData = producto.precios
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
      .map(p => ({
        fecha: new Date(p.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        precio: p.precio
      }));
    
    const precios = producto.precios.map(p => p.precio);
    const media = precios.reduce((a, b) => a + b, 0) / precios.length;
    const min = Math.min(...precios);
    const max = Math.max(...precios);
    const variacion = precios.length > 1 
      ? ((precios[0] - precios[precios.length - 1]) / precios[precios.length - 1] * 100)
      : 0;
    
    setPreciosData({
      producto: producto.nombre,
      ultimoPrecio: producto.ultimoPrecio,
      ultimaFecha: producto.ultimaFecha,
      media,
      min,
      max,
      variacion,
      chartData
    });
  }

  useEffect(() => {
    if (selectedProducto) {
      loadPreciosProducto(selectedProducto);
    }
  }, [selectedProducto]);

  function handleToggleFavorito() {
    if (!selectedProducto) return;
    
    const prod = productos.find(p => p.id === parseInt(selectedProducto));
    const isFavorito = favoritos.productoFavorito === prod?.nombre;
    
    const newFavoritos = saveFavoritos({
      productoFavorito: isFavorito ? null : prod?.nombre
    });
    setFavoritos(newFavoritos);
    toast.success(isFavorito ? 'Favorito eliminado' : 'Producto guardado como favorito');
  }

  return (
    <div className="px-4 py-4" data-testid="precios-page">
      <PageHeader 
        title="Precios Mercatenerife" 
        subtitle={loading || loadingMeses ? 'Cargando...' : `${productos.length} productos · ${MESES_NOMBRES[selectedMonth] || ''} ${selectedYear}`}
        showBack
      />

      {/* Selectores de año y mes */}
      <div className="mt-4 flex gap-2">
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[100px] bg-white" data-testid="select-year">
            <Calendar className="h-4 w-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEARS_AVAILABLE.map(year => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select 
          value={selectedMonth?.toString() || ''} 
          onValueChange={(v) => setSelectedMonth(parseInt(v))}
          disabled={availableMonths.length === 0 || loadingMeses}
        >
          <SelectTrigger className="w-[140px] bg-white" data-testid="select-month">
            <SelectValue placeholder={loadingMeses ? "Cargando..." : "Seleccionar"} />
          </SelectTrigger>
          <SelectContent>
            {availableMonths.map(month => (
              <SelectItem key={month} value={month.toString()}>
                {MESES_NOMBRES[month]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button 
          variant="outline" 
          size="icon" 
          onClick={loadProductos} 
          aria-label="Recargar datos de precios"
          className="focus-visible:ring-2 focus-visible:ring-primary"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* Pestañas */}
      <div className="flex gap-2 mt-4" role="tablist">
        <Button
          variant="default"
          size="sm"
          role="tab"
          aria-selected="true"
        >
          Listado de precios
        </Button>
      </div>

      {/* Texto descriptivo */}
      <p className="text-sm text-muted-foreground mt-3" id="precios-descripcion">
        Seleccione un producto para ver un análisis y evolución de su precio
      </p>

      {loading ? (
        <div className="mt-6" aria-busy="true">
          <LoadingState message={`Cargando precios de ${MESES_NOMBRES[selectedMonth]} ${selectedYear}...`} />
        </div>
      ) : error ? (
        <div className="mt-6" role="alert">
          <ErrorState message={error} onRetry={loadProductos} />
        </div>
      ) : (
        <>
          {/* Contenido de Listado de precios (análisis por producto) */}
          <div className="mt-4 space-y-3" role="tabpanel" aria-describedby="precios-descripcion">
            {/* Buscador de producto */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder="Buscar producto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-white"
                data-testid="search-producto"
                aria-label="Buscar producto por nombre"
              />
            </div>

            {/* Fecha de los datos */}
            {productos.length > 0 && productos[0].ultimaFecha && (
              <p className="text-sm text-muted-foreground" aria-live="polite">
                Precios del {new Date(productos[0].ultimaFecha).toLocaleDateString('es-ES', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                })}
              </p>
            )}
            
            {/* Lista de productos */}
            <ScrollArea className="h-[200px] border rounded-lg bg-white" aria-label="Lista de productos">
              <div className="p-2 space-y-1" role="listbox" aria-label="Seleccionar producto">
                {filteredProductos.map(producto => {
                  const isSelected = selectedProducto === producto.id.toString();
                  return (
                    <Button
                      key={producto.id}
                      variant={isSelected ? 'default' : 'ghost'}
                      className="w-full justify-between h-auto py-2 focus-visible:ring-2 focus-visible:ring-primary"
                      onClick={() => setSelectedProducto(producto.id.toString())}
                      data-testid={`producto-${producto.id}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-label={`${producto.nombre}, precio ${producto.ultimoPrecio.toFixed(2)} euros por kilo`}
                    >
                      <span className="truncate">{producto.nombre}</span>
                      <Badge variant="outline" className={`ml-2 ${isSelected ? 'text-white border-white/50' : ''}`} aria-hidden="true">
                        {producto.ultimoPrecio.toFixed(2)}€
                      </Badge>
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Detalle del producto seleccionado */}
            {selectedProducto && preciosData && (
              <section className="space-y-4" aria-label={`Análisis de precios de ${preciosData.producto}`}>
                {/* Header con nombre y acciones */}
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-lg">{preciosData.producto}</h2>
                  <div className="flex gap-2" role="toolbar" aria-label="Acciones del producto">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        const csvContent = preciosData.chartData
                          .map(d => `${d.fecha},${d.precio}`)
                          .join('\n');
                        const blob = new Blob([`Fecha,Precio\n${csvContent}`], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `precios_${preciosData.producto.replace(/\s+/g, '_')}.csv`;
                        link.click();
                        toast.success('CSV descargado');
                      }}
                      aria-label={`Descargar datos de ${preciosData.producto} en formato CSV`}
                      className="focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <Download size={14} className="mr-1" aria-hidden="true" />
                      Descargar CSV
                    </Button>
                    {(() => {
                      const prod = productos.find(p => p.id === parseInt(selectedProducto));
                      const isFav = favoritos.productoFavorito === prod?.nombre;
                      return (
                        <Button 
                          variant={isFav ? "default" : "outline"}
                          size="sm" 
                          onClick={handleToggleFavorito}
                          data-testid="set-favorito-btn"
                          aria-label={isFav ? 'Eliminar de favoritos' : 'Guardar como favorito'}
                          aria-pressed={isFav}
                          className="focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <Star size={14} className={isFav ? "fill-current mr-1" : "mr-1"} aria-hidden="true" />
                          {isFav ? 'Favorito' : 'Guardar'}
                        </Button>
                      );
                    })()}
                  </div>
                </div>

                {/* Precio actual con fecha */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground" id="ultimo-precio-label">Último precio</p>
                        <p className="text-3xl font-bold" aria-labelledby="ultimo-precio-label">{preciosData.ultimoPrecio.toFixed(2)} €/kg</p>
                        {preciosData.ultimaFecha && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(preciosData.ultimaFecha).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                      <div 
                        className={`flex items-center ${preciosData.variacion >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        aria-label={`Variación de precio: ${preciosData.variacion >= 0 ? 'subió' : 'bajó'} ${Math.abs(preciosData.variacion).toFixed(1)} por ciento`}
                      >
                        {preciosData.variacion >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                        <span className="text-lg font-semibold ml-1">
                          {preciosData.variacion >= 0 ? '+' : ''}{preciosData.variacion.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Estadísticas */}
                <div className="grid grid-cols-3 gap-2" role="list" aria-label="Estadísticas de precio">
                  <Card role="listitem">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground" id="stat-media">Media</p>
                      <p className="text-lg font-semibold" aria-labelledby="stat-media">{preciosData.media.toFixed(2)}€</p>
                    </CardContent>
                  </Card>
                  <Card role="listitem">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground" id="stat-min">Mínimo</p>
                      <p className="text-lg font-semibold text-green-600" aria-labelledby="stat-min">{preciosData.min.toFixed(2)}€</p>
                    </CardContent>
                  </Card>
                  <Card role="listitem">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground" id="stat-max">Máximo</p>
                      <p className="text-lg font-semibold text-red-600" aria-labelledby="stat-max">{preciosData.max.toFixed(2)}€</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Gráfica */}
                {preciosData.chartData.length > 1 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm" id="chart-title">Evolución del precio</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="h-[200px]" 
                        role="img" 
                        aria-labelledby="chart-title"
                        aria-describedby="chart-description"
                      >
                        <span id="chart-description" className="sr-only">
                          Gráfico de área que muestra la evolución del precio de {preciosData.producto} 
                          desde {preciosData.chartData[0]?.fecha} hasta {preciosData.chartData[preciosData.chartData.length - 1]?.fecha}
                        </span>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={preciosData.chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                            <YAxis 
                              tick={{ fontSize: 10 }} 
                              domain={['auto', 'auto']}
                              tickFormatter={(v) => `${v.toFixed(1)}€`}
                            />
                            <Tooltip 
                              formatter={(value) => [`${value.toFixed(2)} €/kg`, 'Precio']}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="precio" 
                              stroke="#10b981" 
                              fill="#10b98133"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PreciosPage;
